import { Fork, Invoke } from '@swf/components/StarsWizard/Flow';
import useWizardExit from '@swf/core/hooks/useWizardExit';
import type { FlowConfig } from '@swf/core/types/Wizard';
import { Action } from '@swf/core/types/Wizard';

import RMFDepositLimit from '../../components/RMFDepositLimit/RMFDepositLimit';
import RMFPhone from '../../components/RMFPhone/RMFPhone';
import RMFPhoneVerification from '../../components/RMFPhoneVerification/RMFPhoneVerification';
import RMFSSN from '../../components/RMFSSN/RMFSSN';
import RMFSSNConfirmation from '../../components/RMFSSN/components/RMFSSNConfirmation/RMFSSNConfirmation';
import RMFStandaloneFinalPage from '../../components/RMFStandaloneFinalPage/RMFStandaloneFinalPage';
import RMFSummaryScreenUSPA from '../../components/RMFSummaryScreenUSPA/RMFSummaryScreenUSPA';
import RMFTermsOfService from '../../components/RMFTermsOfService/RMFTermsOfService';
import RMFUSPAFinishScreen from '../../components/RMFUSPAFinishScreen/RMFUSPAFinishScreen';
import RMFVerificationUSPA from '../../components/RMFVerificationUSPA/RMFVerificationUSPA';
import useRMFBaseSteps from '../useRMFBaseSteps';
import { Guard } from '../useRmfGuards';
import { Service } from '../useRmfServices';

const useRmfUSPAFlow = (): FlowConfig => {
  const exitWizard = useWizardExit();

  const jumpToFeatureStep = {
    name: 'jumpToFeatureStep',
    entry: Action.GoJump
  };

  const { forkSignupDepositCheckDone } = useRMFBaseSteps();

  const forkJumpToFeature = new Fork(
    Guard.DoesJumpToFeatureStep,
    [new Invoke(Service.SetJumpCreator, jumpToFeatureStep)],
    [RMFUSPAFinishScreen, forkSignupDepositCheckDone]
  );

  const forkRmfExit = new Fork(
    Guard.StandaloneRmf,
    [
      RMFStandaloneFinalPage,
      {
        name: 'exitRMF',
        entry: exitWizard,
        type: 'final'
      }
    ],
    [RMFUSPAFinishScreen, forkJumpToFeature]
  );

  const invokePhoneVerificationSave = new Invoke(
    Service.RmfMobilePhoneVerificationSave,
    RMFVerificationUSPA,
    RMFPhoneVerification
  );

  const forkShouldSkipSMSValidation = new Fork(
    Guard.ShouldSkipSMSValidation,
    [RMFVerificationUSPA],
    [RMFPhoneVerification, invokePhoneVerificationSave]
  );

  const forkPhoneVerificationCheck = new Fork(
    Guard.PhoneVerificationCheck,
    [forkShouldSkipSMSValidation],
    [RMFVerificationUSPA]
  );

  const invokeUSPAUserDataSave = new Invoke(Service.RmfUSPAUserDataSave, RMFSSN);

  const invokeRmfDataReload = new Invoke(Service.RmfUpdateUSPAUserData, RMFSummaryScreenUSPA);
  const invokeMobilePhoneSave = new Invoke(
    Service.RmfMobilePhoneSave,
    forkPhoneVerificationCheck,
    RMFPhone
  );

  const invokeDepositLimitSave = new Invoke(
    Service.RmfDepositLimitSave,
    forkRmfExit,
    RMFDepositLimit
  );
  const forkDepositLimits = new Fork(
    Guard.LimitsSelected,
    [forkRmfExit],
    [invokeDepositLimitSave, forkRmfExit]
  );

  const forkSSNValidationCheck = new Fork(
    Guard.IsSSNConfirmationRequired,
    [RMFSSNConfirmation, RMFDepositLimit],
    [RMFDepositLimit]
  );

  const invokeUSPASSNConfirm = new Invoke(
    Service.RmfUSPASSNConfirm,
    forkSSNValidationCheck,
    RMFSSN
  );

  return [
    RMFPhone,
    invokeMobilePhoneSave,
    forkPhoneVerificationCheck,
    RMFVerificationUSPA,
    invokeRmfDataReload,
    RMFSummaryScreenUSPA,
    invokeUSPAUserDataSave,
    RMFSSN,
    invokeUSPASSNConfirm,
    forkSSNValidationCheck,
    RMFTermsOfService,
    forkDepositLimits,
    forkRmfExit,
    jumpToFeatureStep
  ];
};

export default useRmfUSPAFlow;
